/**
 * Messages — SMS and WhatsApp from the portal.
 *
 * Three tabs: Compose (pick an audience, see the cost, send), History (what
 * went out and whether it was delivered), Settings (the gateway key and sender
 * ID, so nobody needs server access to change them).
 *
 * A send spends real money and reaches real people, so nothing goes out until
 * the admin has seen the recipient count and the cost and confirmed.
 *
 * Note: every form here is plain JSX in variables, never a component declared
 * inside this one — that would remount the inputs on each keystroke.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronRight, MessageSquare, RefreshCw,
  Search, Send, Settings as SettingsIcon, UserPlus, Wallet, X,
} from 'lucide-react';

import {
  fetchSmsBalance, fetchSmsCampaign, fetchSmsCampaigns, fetchSmsSegments, fetchSmsSettings,
  previewSmsSend, refreshSmsCampaign, resendFailedSms, searchSmsRecipients, sendSmsCampaign,
  testSmsConnection, updateSmsSettings,
} from './hooks/hooks';

const NAV = '#001f3f';

const SELECTED = 'selected';

const TABS = [
  { key: 'compose', label: 'Compose' },
  { key: 'history', label: 'History' },
  { key: 'settings', label: 'Settings' },
];

const CHANNEL_LABELS = { mobile: 'SMS', whatsapp: 'WhatsApp' };

const money = (value) =>
  `TZS ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const errorText = (err, fallback) =>
  err?.response?.data?.detail || err?.response?.data?.error || fallback;

const STATUS_STYLES = {
  sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  sending: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
};

const Pill = ({ value }) => (
  <span className={`inline-block text-[11px] font-semibold border rounded-full px-2 py-0.5 ${
    STATUS_STYLES[value] || STATUS_STYLES.draft}`}>
    {value}
  </span>
);

const Toast = ({ toast, onClose }) => {
  if (!toast.open) return null;
  const cls = toast.severity === 'success'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-red-50 text-red-700 border-red-200';
  return (
    <div className="fixed top-4 right-4 z-50">
      <div className={`${cls} border rounded-lg px-4 py-3 shadow-lg flex items-start gap-2 max-w-sm`}>
        <span className="text-sm font-medium">{toast.message}</span>
        <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-600">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

function Messages() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('compose');
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });

  const ok = (message) => setToast({ open: true, message, severity: 'success' });
  const bad = (message) => setToast({ open: true, message, severity: 'error' });

  // ── compose state ──────────────────────────────────────────────────────────
  const [segment, setSegment] = useState('all');
  const [channel, setChannel] = useState('mobile');
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [chosenPeople, setChosenPeople] = useState([]);   // whole rows, so names can be shown
  const [personSearch, setPersonSearch] = useState('');

  // ── settings state ─────────────────────────────────────────────────────────
  const [apiKey, setApiKey] = useState('');
  const [senderId, setSenderId] = useState('');
  const [threshold, setThreshold] = useState('');
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [openCampaign, setOpenCampaign] = useState(null);

  const balance = useQuery({ queryKey: ['sms-balance'], queryFn: fetchSmsBalance, retry: false });
  const segments = useQuery({ queryKey: ['sms-segments'], queryFn: fetchSmsSegments, retry: false });
  // Opening History refreshes the delivery figures from the gateway, so a
  // delivered message stops reading as pending without anyone pressing a button.
  const campaigns = useQuery({
    queryKey: ['sms-campaigns'], queryFn: fetchSmsCampaigns, retry: false,
    enabled: tab === 'history',
    refetchOnMount: 'always',
  });
  const settings = useQuery({
    queryKey: ['sms-settings'], queryFn: fetchSmsSettings, retry: false,
    enabled: tab === 'settings',
  });

  // react-query v5 dropped useQuery's onSuccess, so the stored values are
  // seeded here. Without this the Sender ID field stayed empty and saving any
  // other field would have cleared the stored sender ID.
  useEffect(() => {
    if (settingsLoaded || !settings.data) return;
    setSenderId(settings.data.sender_id || '');
    setThreshold(String(settings.data.low_balance_threshold_tzs ?? ''));
    setSettingsLoaded(true);
  }, [settings.data, settingsLoaded]);

  // Only searches once the admin is actually picking people.
  const people = useQuery({
    queryKey: ['sms-recipients', personSearch],
    queryFn: () => searchSmsRecipients(personSearch),
    enabled: segment === SELECTED,
    retry: false,
  });

  const campaignDetail = useQuery({
    queryKey: ['sms-campaign', openCampaign],
    queryFn: () => fetchSmsCampaign(openCampaign),
    enabled: !!openCampaign,
    retry: false,
  });

  const previewMutation = useMutation({
    mutationFn: previewSmsSend,
    onSuccess: setPreview,
    onError: (err) => bad(errorText(err, 'Could not work out who this would reach.')),
  });

  const sendMutation = useMutation({
    mutationFn: sendSmsCampaign,
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: ['sms-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['sms-balance'] });
      setConfirming(false);
      setBody('');
      setName('');
      setPreview(null);
      setChosenPeople([]);
      setPersonSearch('');
      ok(`Sent to ${campaign.recipient_count} ${campaign.recipient_count === 1 ? 'person' : 'people'} — ${money(campaign.cost_tzs)}.`);
      setTab('history');
    },
    onError: (err) => {
      setConfirming(false);
      bad(errorText(err, 'The message could not be sent.'));
    },
  });

  const settingsMutation = useMutation({
    mutationFn: updateSmsSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms-settings'] });
      queryClient.invalidateQueries({ queryKey: ['sms-balance'] });
      setApiKey('');
      ok('Saved. The gateway uses it from now on.');
    },
    onError: (err) => bad(errorText(err, 'Could not save the settings.')),
  });

  const testMutation = useMutation({
    mutationFn: testSmsConnection,
    onSuccess: (data) => ok(`${data.detail} Balance: ${money(data.balance?.balance_tzs)}.`),
    onError: (err) => bad(errorText(err, 'The key did not work.')),
  });

  const refreshMutation = useMutation({
    mutationFn: refreshSmsCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms-campaign', openCampaign] });
      queryClient.invalidateQueries({ queryKey: ['sms-campaigns'] });
      ok('Delivery status updated.');
    },
    onError: (err) => bad(errorText(err, 'Could not fetch the delivery status.')),
  });

  const resendMutation = useMutation({
    mutationFn: resendFailedSms,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms-campaign', openCampaign] });
      queryClient.invalidateQueries({ queryKey: ['sms-balance'] });
      ok('Resent to the numbers that failed.');
    },
    onError: (err) => bad(errorText(err, 'Could not resend.')),
  });

  // ── derived ────────────────────────────────────────────────────────────────
  const balanceData = balance.data;

  // The backend decides which channels are available. WhatsApp is switched off
  // for now, so the picker and its figures stay hidden rather than offering
  // something the API would refuse.
  const channels = useMemo(() => balanceData?.channels || ['mobile'], [balanceData]);
  const whatsappOn = !!balanceData?.whatsapp_enabled;

  const audienceOptions = useMemo(() => {
    const groups = {};
    for (const row of segments.data?.segments || []) {
      (groups[row.group] = groups[row.group] || []).push(row);
    }
    for (const row of segments.data?.communities || []) {
      (groups[row.group] = groups[row.group] || []).push(row);
    }
    return groups;
  }, [segments.data]);

  const chosen = useMemo(() => {
    const all = [...(segments.data?.segments || []), ...(segments.data?.communities || [])];
    return all.find((row) => row.key === segment);
  }, [segments.data, segment]);

  // SMS parts: 160 GSM characters each, 70 once the text carries unicode.
  const parts = useMemo(() => {
    if (!body) return 0;
    // eslint-disable-next-line no-control-regex -- GSM-7 is the ASCII range
    const unicode = /[^\u0000-\u007F]/.test(body);
    return Math.ceil(body.length / (unicode ? 70 : 160));
  }, [body]);

  const pickedIds = chosenPeople.map((person) => person.id);
  const pickingPeople = segment === SELECTED;

  const canSend = !!segment && !!name.trim() && !!body.trim() && !sendMutation.isLoading
    && (!pickingPeople || pickedIds.length > 0);

  const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';
  const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2';

  // If the available channels change under us, fall back to the first one.
  useEffect(() => {
    if (channels.length && !channels.includes(channel)) setChannel(channels[0]);
  }, [channels, channel]);

  // user_ids only travels when people were picked by hand; a segment send has
  // no business carrying an empty list.
  const picked = () => (pickingPeople ? { user_ids: pickedIds } : {});

  const askPreview = () => {
    setConfirming(true);
    previewMutation.mutate({ segment, body, channel, ...picked() });
  };

  const doSend = () => sendMutation.mutate({
    segment, name: name.trim(), body, channel, ...picked(), confirm: true,
  });

  const togglePerson = (person) => setChosenPeople((current) =>
    current.some((row) => row.id === person.id)
      ? current.filter((row) => row.id !== person.id)
      : [...current, person]);

  // ── balance strip ──────────────────────────────────────────────────────────
  const unconfigured = balanceData && balanceData.configured === false;
  const low = balanceData?.is_low;

  const balanceStrip = (
    <div className={`rounded-xl border p-4 flex flex-wrap items-center gap-4 ${
      unconfigured || low ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center gap-2">
        <Wallet size={16} style={{ color: NAV }} />
        <div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">SMS balance</p>
          {balance.isLoading ? (
            <p className="text-sm text-slate-400">Checking…</p>
          ) : unconfigured ? (
            <p className="text-sm font-semibold text-amber-700">No API key set yet</p>
          ) : balance.isError ? (
            <p className="text-sm font-semibold text-red-600">
              {errorText(balance.error, 'Gateway unreachable')}
            </p>
          ) : (
            <p className="text-lg font-bold text-slate-800 leading-tight">
              {money(balanceData?.balance_tzs)}
            </p>
          )}
        </div>
      </div>

      {!unconfigured && !balance.isError && balanceData && (
        <>
          <div className="h-8 w-px bg-slate-200" />
          <div className="text-xs text-slate-600">
            <span className="font-semibold">{balanceData.sms_units_equivalent ?? '—'}</span> SMS
            {whatsappOn ? (
              <>
                <span className="text-slate-400"> · </span>
                <span className="font-semibold">{balanceData.whatsapp_units_equivalent ?? '—'}</span> WhatsApp
              </>
            ) : null}
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div className="text-xs text-slate-600">
            {money(balanceData.price_per_sms)} per SMS
            {whatsappOn ? ` · ${money(balanceData.whatsapp_price_per_part)} per WhatsApp part` : ''}
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div className="text-xs text-slate-600">Sender: <span className="font-semibold">{balanceData.sender_id}</span></div>
        </>
      )}

      {low ? (
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700">
          <AlertTriangle size={13} /> Running low — top up
        </span>
      ) : null}

      <button
        onClick={() => balance.refetch()}
        className="ml-auto flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 bg-white"
      >
        <RefreshCw size={13} /> Refresh
      </button>
    </div>
  );

  // ── compose tab ────────────────────────────────────────────────────────────
  const composeTab = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
      <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        <div>
          <label className={labelCls}>Who should get this</label>
          <select className={inputCls} value={segment} onChange={(e) => { setSegment(e.target.value); setPreview(null); }}>
            <optgroup label="Pick people">
              <option value={SELECTED}>Specific people…</option>
            </optgroup>
            {Object.entries(audienceOptions).map(([group, rows]) => (
              <optgroup key={group} label={group}>
                {rows.map((row) => (
                  <option key={row.key} value={row.key}>
                    {row.label}
                    {row.reachable !== undefined ? ` — ${row.reachable} reachable of ${row.total}` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {!pickingPeople && chosen?.reachable !== undefined && chosen.total > chosen.reachable ? (
            <p className="text-[11px] text-amber-700 mt-2">
              {chosen.total - chosen.reachable} of {chosen.total} in this group have no phone number on file and will be skipped.
            </p>
          ) : null}
        </div>

        {pickingPeople ? (
          <div className="border border-slate-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                <UserPlus size={13} /> Chosen
                <span className="text-slate-400 font-normal">
                  {chosenPeople.length === 0 ? 'nobody yet' : `${chosenPeople.length} selected`}
                </span>
              </p>
              {chosenPeople.length > 0 ? (
                <button onClick={() => { setChosenPeople([]); setPreview(null); }}
                        className="text-[11px] font-semibold text-slate-500 hover:text-slate-700">
                  Clear all
                </button>
              ) : null}
            </div>

            {chosenPeople.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {chosenPeople.map((person) => (
                  <span key={person.id}
                        className={`inline-flex items-center gap-1.5 text-xs rounded-full border px-2.5 py-1 ${
                          person.reachable
                            ? 'bg-slate-50 border-slate-200 text-slate-700'
                            : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                    {person.name}
                    {!person.reachable ? ' · no number' : ''}
                    <button onClick={() => { togglePerson(person); setPreview(null); }}
                            title={`Remove ${person.name}`}
                            className="text-slate-400 hover:text-red-600">
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}

            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className={`${inputCls} pl-9`}
                value={personSearch}
                onChange={(e) => setPersonSearch(e.target.value)}
                placeholder="Search by name, email or phone number"
              />
            </div>

            <div className="max-h-56 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
              {people.isLoading ? (
                <p className="text-xs text-slate-400 p-3">Searching…</p>
              ) : (people.data?.results || []).length === 0 ? (
                <p className="text-xs text-slate-400 p-3">Nobody matches that.</p>
              ) : (
                (people.data?.results || []).map((person) => {
                  const picked = pickedIds.includes(person.id);
                  return (
                    <button
                      key={person.id}
                      onClick={() => { togglePerson(person); setPreview(null); }}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                        picked ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-slate-800 truncate">{person.name}</span>
                        <span className="block text-[11px] text-slate-400 truncate">
                          {person.phone || 'no phone number on file'} · {person.email}
                        </span>
                      </span>
                      {picked ? <CheckCircle2 size={15} className="text-teal-600 flex-shrink-0" /> : null}
                    </button>
                  );
                })
              )}
            </div>

            {chosenPeople.some((person) => !person.reachable) ? (
              <p className="text-[11px] text-amber-700">
                Some of the people chosen have no phone number and will be skipped.
              </p>
            ) : null}
          </div>
        ) : null}

        {channels.length > 1 ? (
          <div>
            <label className={labelCls}>Channel</label>
            <div className="flex gap-2">
              {channels.map((key) => {
                const active = channel === key;
                return (
                  <button
                    key={key}
                    onClick={() => { setChannel(key); setPreview(null); }}
                    className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${
                      active ? 'text-white border-transparent' : 'text-slate-600 bg-white border-slate-200 hover:bg-slate-50'}`}
                    style={active ? { backgroundColor: NAV } : undefined}
                  >
                    {CHANNEL_LABELS[key] || key}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div>
          <label className={labelCls}>Label for your records</label>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. October contribution reminder" maxLength={140} />
        </div>

        <div>
          <label className={labelCls}>Message</label>
          <textarea className={inputCls} rows={6} value={body}
                    onChange={(e) => { setBody(e.target.value); setPreview(null); }}
                    placeholder="Write the message exactly as it should arrive…" />
          <div className="flex items-center justify-between mt-2">
            <p className="text-[11px] text-slate-400">
              {body.length} characters · {parts} {parts === 1 ? 'part' : 'parts'} per recipient
            </p>
            {parts > 1 ? (
              <p className="text-[11px] font-semibold text-amber-700">
                Longer than one part — each part is charged separately
              </p>
            ) : null}
          </div>
        </div>

        <button
          onClick={askPreview}
          disabled={!canSend}
          className="flex items-center gap-2 bg-teal-600 text-white py-2.5 px-6 rounded-lg font-semibold text-sm hover:bg-teal-700 transition-colors disabled:opacity-50"
        >
          <Send size={14} /> Review and send
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <p className="text-xs font-bold text-slate-700 mb-1">How it will arrive</p>
        <p className="text-[11px] text-slate-400 mb-4">
          {CHANNEL_LABELS[channel] || channel} from {balanceData?.sender_id || 'your sender ID'}
        </p>
        <div className="rounded-lg bg-slate-900 p-4">
          <p className="text-[13px] leading-relaxed text-slate-100 whitespace-pre-wrap break-words">
            {body || 'Your message will appear here.'}
          </p>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">
          Personal names are not inserted — write it so it reads correctly for everyone in the group.
        </p>
      </div>
    </div>
  );

  // ── confirm dialog ─────────────────────────────────────────────────────────
  const confirmDialog = confirming && (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-600" />
          <h3 className="text-sm font-bold text-slate-800">Send this message?</h3>
        </div>

        <div className="p-6 space-y-3">
          {previewMutation.isLoading ? (
            <p className="text-sm text-slate-500">Working out who this reaches…</p>
          ) : preview ? (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Audience</span>
                <span className="font-semibold text-slate-800">{preview.segment_label}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Will receive it</span>
                <span className="font-semibold text-slate-800">{preview.recipients}</span>
              </div>
              {preview.skipped_no_phone > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Skipped (no phone number)</span>
                  <span className="font-semibold text-amber-700">{preview.skipped_no_phone}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Cost</span>
                <span className="font-semibold text-slate-800">
                  {preview.estimate ? money(preview.estimate.cost) : '—'}
                  {preview.estimate ? ` · ${preview.estimate.parts} part${preview.estimate.parts === 1 ? '' : 's'}` : ''}
                </span>
              </div>
              {preview.estimate_error ? (
                <p className="text-[11px] text-amber-700">{preview.estimate_error}</p>
              ) : null}
              {preview.recipients === 0 ? (
                <p className="text-sm font-semibold text-red-600">
                  Nobody in this group has a usable phone number.
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-slate-500">—</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button onClick={() => setConfirming(false)}
                  className="text-sm font-medium text-slate-500 hover:text-slate-700">
            Cancel
          </button>
          <button
            onClick={doSend}
            disabled={sendMutation.isLoading || previewMutation.isLoading || !preview || preview.recipients === 0}
            className="flex items-center gap-2 bg-teal-600 text-white py-2 px-5 rounded-lg font-semibold text-sm hover:bg-teal-700 disabled:opacity-50"
          >
            <Send size={14} />
            {sendMutation.isLoading ? 'Sending…' : `Send to ${preview?.recipients ?? 0}`}
          </button>
        </div>
      </div>
    </div>
  );

  // ── history tab ────────────────────────────────────────────────────────────
  const detail = campaignDetail.data;

  const historyTab = openCampaign ? (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
        <button onClick={() => setOpenCampaign(null)}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800">
          <ArrowLeft size={16} /> <span className="font-medium">Back to history</span>
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => refreshMutation.mutate(openCampaign)}
                  disabled={refreshMutation.isLoading}
                  className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw size={13} /> Check again
          </button>
          {detail?.failed_count > 0 && (
            <button onClick={() => resendMutation.mutate(openCampaign)}
                    disabled={resendMutation.isLoading}
                    className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50"
                    style={{ backgroundColor: NAV }}>
              <Send size={13} /> Resend {detail.failed_count} failed
            </button>
          )}
        </div>
      </div>

      {campaignDetail.isLoading ? (
        <div className="p-10 text-center text-slate-400">Loading…</div>
      ) : detail ? (
        <div className="p-5 space-y-5">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-slate-800">{detail.name}</h3>
              <Pill value={detail.status} />
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {detail.segment_label} · {detail.channel_label} · {detail.sent_by_name || '—'} ·{' '}
              {detail.sent_at ? new Date(detail.sent_at).toLocaleString() : 'not sent'}
            </p>
          </div>

          <p className="text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-lg p-4 whitespace-pre-wrap">
            {detail.body}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              ['Recipients', detail.recipient_count],
              ['Delivered', detail.delivered_count],
              ['Pending', detail.pending_count],
              ['Failed', detail.failed_count],
              ['Cost', money(detail.cost_tzs)],
            ].map(([label, value]) => (
              <div key={label} className="border border-slate-200 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
                <p className="text-sm font-bold text-slate-800 mt-0.5">{value}</p>
              </div>
            ))}
          </div>

          {detail.error ? (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{detail.error}</p>
          ) : null}

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-slate-50">
                <tr>
                  {['Name', 'Number', 'Status', 'Note'].map((h) => (
                    <th key={h} className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-4 py-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(detail.recipients || []).map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2 text-sm text-slate-700">{row.name || '—'}</td>
                    <td className="px-4 py-2 text-sm text-slate-500 font-mono">{row.number}</td>
                    <td className="px-4 py-2"><Pill value={row.status} /></td>
                    <td className="px-4 py-2 text-xs text-slate-400">{row.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  ) : (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {campaigns.isLoading ? (
        <div className="p-10 text-center text-slate-400">Loading…</div>
      ) : (campaigns.data || []).length === 0 ? (
        <div className="p-10 text-center text-slate-400 text-sm">Nothing has been sent yet.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {(campaigns.data || []).map((row) => (
            <button key={row.id} onClick={() => setOpenCampaign(row.id)}
                    className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-800 truncate">{row.name}</p>
                  <Pill value={row.status} />
                </div>
                <p className="text-xs text-slate-400 truncate mt-0.5">
                  {row.segment_label} · {row.channel_label} · {row.recipient_count} sent ·{' '}
                  {row.delivered_count} delivered · {row.failed_count} failed · {money(row.cost_tzs)}
                </p>
              </div>
              <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ── settings tab ───────────────────────────────────────────────────────────
  const settingsData = settings.data;

  const settingsTab = (
    <div className="max-w-2xl bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-2">
        <SettingsIcon size={16} style={{ color: NAV }} />
        <div>
          <h3 className="text-sm font-bold text-slate-800">Gateway settings</h3>
          <p className="text-xs text-slate-400">SNotify — api.snotify.co.tz</p>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div>
          <label className={labelCls}>API key</label>
          <input
            type="password"
            className={inputCls}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={settingsData?.api_key_set ? 'Enter a new key to replace the current one' : 'Paste the key from the SNotify Developer page'}
            autoComplete="new-password"
          />
          <p className="text-[11px] text-slate-400 mt-2">
            {settingsData?.key_source === 'portal' && (
              <>In use, set here: <span className="font-mono">{settingsData.api_key_hint}</span>. Leave blank to keep it.</>
            )}
            {settingsData?.key_source === 'environment' && 'Currently taken from the server environment. A key entered here overrides it.'}
            {settingsData?.key_source === 'none' && 'No key set — sending is disabled until one is saved.'}
          </p>
        </div>

        <div>
          <label className={labelCls}>Sender ID <span className="text-slate-400 normal-case">(optional)</span></label>
          {(settingsData?.available_sender_ids || []).length > 0 ? (
            <select className={inputCls} value={senderId} onChange={(e) => setSenderId(e.target.value)}>
              <option value="">SNOTIFY (shared name)</option>
              {settingsData.available_sender_ids.map((row) => (
                <option key={row.id} value={String(row.id)} disabled={row.status && row.status !== 'approved'}>
                  {row.name || `Sender ${row.id}`}
                  {row.is_default ? ' — used by default' : ''}
                  {row.status && row.status !== 'approved' ? ` — ${row.status}` : ''}
                </option>
              ))}
            </select>
          ) : (
            <input className={inputCls} value={senderId} onChange={(e) => setSenderId(e.target.value)}
                   placeholder="e.g. 12" inputMode="numeric" maxLength={12} />
          )}
          <p className="text-[11px] text-slate-400 mt-2">
            Messages go out as <strong>SNOTIFY</strong> unless your own sender name has been
            approved — that is normal, and sending works without one. Once approved, SNotify
            identifies it by a <strong>number</strong>, not the name, so enter that number here.
          </p>
          {senderId && !/^\d+$/.test(senderId) ? (
            <p className="text-[11px] text-amber-700 mt-1">
              That looks like a name. Enter the number SNotify lists for it, or leave this blank.
            </p>
          ) : null}
        </div>

        <div>
          <label className={labelCls}>Warn me below (TZS)</label>
          <input type="number" min="0" className={inputCls} value={threshold}
                 onChange={(e) => setThreshold(e.target.value)} placeholder="0" />
          <p className="text-[11px] text-slate-400 mt-2">0 uses the gateway's own low-balance threshold.</p>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={() => settingsMutation.mutate({
              ...(apiKey ? { api_key: apiKey } : {}),
              sender_id: senderId,
              low_balance_threshold_tzs: threshold === '' ? 0 : threshold,
            })}
            disabled={settingsMutation.isLoading}
            className="bg-teal-600 text-white py-2.5 px-6 rounded-lg font-semibold text-sm hover:bg-teal-700 disabled:opacity-50"
          >
            {settingsMutation.isLoading ? 'Saving…' : 'Save settings'}
          </button>
          <button
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isLoading}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            <CheckCircle2 size={14} /> {testMutation.isLoading ? 'Checking…' : 'Test connection'}
          </button>
        </div>

        {settingsData?.updated_by_name ? (
          <p className="text-[11px] text-slate-400">
            Last changed by {settingsData.updated_by_name}
            {settingsData.updated_at ? ` on ${new Date(settingsData.updated_at).toLocaleString()}` : ''}.
          </p>
        ) : null}

        {apiKey ? (
          <p className="text-[11px] text-amber-700">
            Saving replaces the key immediately — the old one stops being used.
          </p>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <MessageSquare size={18} style={{ color: NAV }} /> Messages
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Send {whatsappOn ? 'an SMS or WhatsApp message' : 'a text message'} to everyone,
            or to one group or community.
          </p>
        </div>
      </div>

      {balanceStrip}

      <div className="flex items-center gap-1.5">
        {TABS.map((option) => {
          const active = tab === option.key;
          return (
            <button
              key={option.key}
              onClick={() => { setTab(option.key); setOpenCampaign(null); }}
              className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${
                active ? 'text-white border-transparent' : 'text-slate-600 bg-white border-slate-200 hover:bg-slate-50'}`}
              style={active ? { backgroundColor: NAV } : undefined}
            >
              {option.label}
            </button>
          );
        })}
        {segments.isError ? (
          <span className="ml-2 text-xs text-red-600">
            {errorText(segments.error, 'Could not load the audiences.')}
          </span>
        ) : null}
      </div>

      {tab === 'compose' && composeTab}
      {tab === 'history' && historyTab}
      {tab === 'settings' && settingsTab}

      {confirmDialog}
      <Toast toast={toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
    </div>
  );
}

export default Messages;
